/**
 * A day of movements at OEMA, and the stands they use.
 *
 * Placed relative to today at the aerodrome, never written as literals: a
 * flight board with typed-in dates shows an empty day within a fortnight, and
 * an empty day is indistinguishable from an airport with no traffic.
 *
 * ── the awkward cases, on purpose ───────────────────────────────────────
 *
 *  · a **double-booked stand** — two movements overlapping on 104, which is
 *    the commonest real error and the one a plan built in a spreadsheet
 *    misses when a turnaround runs long;
 *  · a **code-E widebody** at an airport with two code-E stands, so "find it
 *    another stand" is a real question rather than a formality;
 *  · an **exclusion pair** — R1 and R2 share a wingtip envelope, and the
 *    platform's own schema calls this the commonest reason a spare stand
 *    turns out not to be spare;
 *  · a **stand closed by a work order that already exists in the sample** —
 *    Stand 85, against WO-000003, the pavement defect. Nothing tells the
 *    flight layer separately; it reads the work orders;
 *  · **late movements with and without a delay code**, because the share
 *    nobody explained is the most useful number in a punctuality report;
 *  · an **estimate that has gone stale**, a **cancellation**, and a
 *    **forecast row** that must never be totalled with the rest.
 *
 * Carriers are the ones that actually serve Madinah. Flight numbers are
 * plausible rather than real: inventing a real airline's real flight number
 * against a fabricated delay would put a fiction against a company that
 * exists.
 */
import type { DelaySource } from "../domain/flightDelays";
import type { Flight, SizeCode } from "../domain/flights";
import type { Stand, StandRegister } from "../domain/stands";

/** Riyadh is UTC+3. */
const OFFSET = 3;
/*
 * The sample's day is the aerodrome's day, not the UTC one. From 21:00 UTC
 * Madinah is already on the next date, and the flight screens read the record
 * by that date — a day placed on the UTC date (the estate clock's TODAY)
 * dropped off them for three hours every night. Midnight at the aerodrome,
 * taken once at import so the day is stable, as TODAY is.
 */
const DAY_START = (() => {
  const d = new Date(Date.now() + OFFSET * 3_600_000);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime() - OFFSET * 3_600_000;
})();

/** An ISO time at a local hour on the sample's own day. */
function at(hour: number, minute = 0, dayOffset = 0): string {
  return new Date(DAY_START + ((dayOffset * 24 + hour) * 60 + minute) * 60_000).toISOString();
}

let seq = 0;
const id = () => `fl-${String(++seq).padStart(3, "0")}`;

interface Row {
  dir: "arrival" | "departure";
  carrier: string;
  number: string;
  other: string;
  icao: string;
  size: SizeCode;
  sched: [number, number];
  /** minutes late, where it has happened or is expected to */
  late?: number;
  happened?: boolean;
  code?: string;
  note?: string;
  turn?: string;
  pax?: number;
  seats?: number;
  service?: Flight["serviceType"];
  cancelled?: string;
  forecast?: boolean;
  /** an estimate entered and then never updated */
  staleEstimate?: boolean;
  /**
   * Minutes from NOW, for the handful of rows that have to be live whenever
   * anybody looks. A board demonstrating "boarding" only between 06:00 and
   * 07:00 demonstrates it to almost nobody, and one that is empty at eleven
   * at night looks broken rather than quiet.
   */
  fromNow?: number;
  /**
   * Delay notices as they arrived: minutes BEFORE the scheduled time the
   * notice came (negative: after it), from whom, and the delay it announced.
   */
  notices?: [number, DelaySource, string, number, string?][];
  /** time has passed and the aircraft has not left: the doors are late */
  doorsOpen?: boolean;
}

const ROWS: Row[] = [
  /* the morning bank: pilgrim traffic, which is what Madinah runs on */
  {
    dir: "arrival",
    carrier: "SV",
    number: "1401",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [5, 40],
    late: 0,
    happened: true,
    turn: "T1",
    pax: 168,
    seats: 180,
  },
  {
    dir: "departure",
    carrier: "SV",
    number: "1402",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [6, 40],
    late: 12,
    happened: true,
    turn: "T1",
    pax: 171,
    seats: 180,
  },
  {
    dir: "arrival",
    carrier: "XY",
    number: "331",
    other: "OERK",
    icao: "A320",
    size: "C",
    sched: [6, 10],
    late: 35,
    happened: true,
    code: "93",
    note: "aircraft rotation — inbound late from Riyadh",
    turn: "T2",
    pax: 154,
    seats: 174,
    notices: [
      [95, "airline_occ", "XY operations control", 25, "93"],
      [40, "airline_occ", "XY operations control", 35, "93"],
    ],
  },
  {
    dir: "departure",
    carrier: "XY",
    number: "332",
    other: "OERK",
    icao: "A320",
    size: "C",
    sched: [7, 5],
    late: 41,
    happened: true,
    code: "93",
    turn: "T2",
    pax: 160,
    seats: 174,
    notices: [[70, "airline_occ", "XY operations control", 41, "93"]],
  },
  {
    dir: "arrival",
    carrier: "MS",
    number: "663",
    other: "HECA",
    icao: "B738",
    size: "C",
    sched: [6, 25],
    late: 0,
    happened: true,
    turn: "T3",
    pax: 176,
    seats: 189,
  },
  {
    dir: "departure",
    carrier: "MS",
    number: "664",
    other: "HECA",
    icao: "B738",
    size: "C",
    sched: [7, 40],
    late: 8,
    happened: true,
    turn: "T3",
    pax: 181,
    seats: 189,
  },

  /* the widebody: one of only two stands on the airport takes it */
  {
    dir: "arrival",
    carrier: "GF",
    number: "8721",
    other: "WIII",
    icao: "B77W",
    size: "E",
    sched: [8, 15],
    late: 20,
    happened: true,
    code: "18",
    note: "load and documentation",
    turn: "T4",
    pax: 344,
    seats: 368,
    notices: [[12, "ground_handler", "Ramp supervisor, Apron 1", 20, "18"]],
  },
  {
    dir: "departure",
    carrier: "GF",
    number: "8722",
    other: "WIII",
    icao: "B77W",
    size: "E",
    sched: [10, 30],
    late: 0,
    turn: "T4",
    pax: 351,
    seats: 368,
  },

  /* midday: the pair that collide on 104 */
  {
    dir: "arrival",
    carrier: "J9",
    number: "617",
    other: "OKKK",
    icao: "A320",
    size: "C",
    sched: [11, 20],
    late: 0,
    happened: true,
    turn: "T5",
    pax: 149,
    seats: 174,
  },
  {
    dir: "departure",
    carrier: "J9",
    number: "618",
    other: "OKKK",
    icao: "A320",
    size: "C",
    sched: [12, 50],
    late: 0,
    turn: "T5",
    pax: 152,
    seats: 174,
  },
  {
    dir: "arrival",
    carrier: "FZ",
    number: "813",
    other: "OMDB",
    icao: "B738",
    size: "C",
    sched: [12, 5],
    late: 25,
    code: "89",
    note: "airport facilities — stand occupied on arrival",
    turn: "T6",
    pax: 165,
    seats: 189,
    notices: [
      [45, "schedule_feed", "Schedule feed", 15],
      [10, "airport_ops", "Airport duty manager", 25, "89"],
    ],
  },
  {
    dir: "departure",
    carrier: "FZ",
    number: "814",
    other: "OMDB",
    icao: "B738",
    size: "C",
    sched: [13, 15],
    late: 30,
    code: "89",
    turn: "T6",
    pax: 170,
    seats: 189,
    notices: [[35, "ground_handler", "Turnaround coordinator", 30, "89"]],
  },

  /* the afternoon, including the one nobody explained */
  {
    dir: "arrival",
    carrier: "TK",
    number: "1907",
    other: "LTFM",
    icao: "A321",
    size: "C",
    sched: [14, 0],
    late: 52,
    happened: true,
    turn: "T7",
    pax: 198,
    seats: 220,
    notices: [[-12, "schedule_feed", "Schedule feed", 52]],
  },
  {
    dir: "departure",
    carrier: "TK",
    number: "1908",
    other: "LTFM",
    icao: "A321",
    size: "C",
    sched: [15, 10],
    late: 58,
    turn: "T7",
    pax: 203,
    seats: 220,
    notices: [[40, "atc", "ATC flow message (slot)", 58]],
  },
  {
    dir: "arrival",
    carrier: "PK",
    number: "743",
    other: "OPLA",
    icao: "A320",
    size: "C",
    sched: [15, 45],
    late: 0,
    turn: "T8",
    pax: 158,
    seats: 174,
  },
  {
    dir: "departure",
    carrier: "PK",
    number: "744",
    other: "OPLA",
    icao: "A320",
    size: "C",
    sched: [17, 0],
    late: 0,
    turn: "T8",
    pax: 161,
    seats: 174,
  },

  /*
   * An estimate somebody entered and never touched again.
   *
   * Its times are placed relative to the moment the sample is built rather
   * than at a fixed hour, because staleness is measured against the clock:
   * pinned to 13:30 it is only stale after about a quarter past two, and a
   * demonstration of a stale estimate that is only true in the afternoon is
   * a fixture that decays every morning. See `mockFlights` below.
   */
  {
    dir: "arrival",
    carrier: "UL",
    number: "225",
    other: "VCBI",
    icao: "A332",
    size: "D",
    sched: [13, 30],
    late: 45,
    staleEstimate: true,
    turn: "T9",
    pax: 246,
    seats: 297,
  },

  /* cancelled, with the reason recorded */
  {
    dir: "departure",
    carrier: "SV",
    number: "1435",
    other: "OEMK",
    icao: "A320",
    size: "C",
    sched: [16, 20],
    cancelled: "Aircraft unserviceable at Jeddah; passengers rebooked on SV1437.",
    seats: 180,
  },

  /* cargo, which parks somewhere else entirely */
  {
    dir: "arrival",
    carrier: "SV",
    number: "9612",
    other: "OEJN",
    icao: "B763",
    size: "D",
    sched: [2, 30],
    late: 0,
    happened: true,
    service: "cargo",
    turn: "C1",
  },
  {
    dir: "departure",
    carrier: "SV",
    number: "9613",
    other: "OEJN",
    icao: "B763",
    size: "D",
    sched: [4, 15],
    late: 0,
    happened: true,
    service: "cargo",
    turn: "C1",
  },

  /* the evening bank, still to come */
  {
    dir: "arrival",
    carrier: "SV",
    number: "1461",
    other: "OERK",
    icao: "A320",
    size: "C",
    sched: [18, 40],
    turn: "T10",
    pax: 166,
    seats: 180,
  },
  {
    dir: "departure",
    carrier: "SV",
    number: "1462",
    other: "OERK",
    icao: "A320",
    size: "C",
    sched: [19, 45],
    turn: "T10",
    pax: 169,
    seats: 180,
  },
  {
    dir: "arrival",
    carrier: "QR",
    number: "1184",
    other: "OTHH",
    icao: "A320",
    size: "C",
    sched: [20, 10],
    turn: "T11",
    pax: 155,
    seats: 174,
  },
  {
    dir: "departure",
    carrier: "QR",
    number: "1185",
    other: "OTHH",
    icao: "A320",
    size: "C",
    sched: [21, 20],
    turn: "T11",
    pax: 158,
    seats: 174,
  },

  /*
   * Two movements anchored to the clock, so the public board always has
   * something live on it and the boarding states are reachable at any hour.
   * Everything else keeps the schedule's own times, which is what a schedule
   * is.
   */
  {
    dir: "departure",
    carrier: "SV",
    number: "1479",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [0, 0],
    fromNow: 28,
    turn: "T12",
    pax: 172,
    seats: 180,
  },
  /*
   * One that flew late with nobody recording why, anchored to the clock for
   * the same reason: the share of unexplained delay is the most useful figure
   * on a punctuality report, and a sample that only contains one between
   * breakfast and lunch demonstrates it to almost nobody.
   */
  {
    dir: "arrival",
    carrier: "MS",
    number: "671",
    other: "HECA",
    icao: "B738",
    size: "C",
    sched: [0, 0],
    fromNow: -95,
    late: 45,
    turn: "T14",
    pax: 173,
    seats: 189,
    notices: [[4, "schedule_feed", "Schedule feed", 45]],
  },
  /* and one that flew late WITH a code, so the two can be told apart at any hour */
  {
    dir: "arrival",
    carrier: "XY",
    number: "355",
    other: "OERK",
    icao: "A320",
    size: "C",
    sched: [0, 0],
    fromNow: -140,
    late: 38,
    code: "93",
    note: "aircraft rotation — inbound late from Riyadh",
    turn: "T15",
    pax: 158,
    seats: 174,
    notices: [[80, "airline_occ", "XY operations control", 38, "93"]],
  },
  /*
   * A delayed departure that runs into the next arrival's stand, and one
   * whose doors should have closed and have not — anchored to the clock so
   * the knock-on and the red state are there whenever anybody looks.
   */
  {
    dir: "departure",
    carrier: "SV",
    number: "1483",
    other: "OEDF",
    icao: "A320",
    size: "C",
    sched: [0, 0],
    fromNow: 25,
    late: 45,
    code: "41",
    note: "aircraft defect — technical check before departure",
    turn: "T16",
    pax: 170,
    seats: 180,
    notices: [
      [55, "airline_occ", "SV operations control", 30, "41"],
      [20, "airline_occ", "SV operations control", 45, "41"],
    ],
  },
  {
    dir: "departure",
    carrier: "SV",
    number: "1491",
    other: "OEAB",
    icao: "A320",
    size: "C",
    sched: [0, 0],
    fromNow: -9,
    doorsOpen: true,
    turn: "T17",
    pax: 177,
    seats: 180,
  },
  {
    dir: "arrival",
    carrier: "SV",
    number: "1478",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [0, 0],
    fromNow: 55,
    turn: "T13",
    pax: 164,
    seats: 180,
  },

  /* tomorrow, as a forecast — never added to today's totals */
  {
    dir: "arrival",
    carrier: "SV",
    number: "1401",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [5, 40],
    forecast: true,
  },
  {
    dir: "departure",
    carrier: "SV",
    number: "1402",
    other: "OEJN",
    icao: "A320",
    size: "C",
    sched: [6, 40],
    forecast: true,
  },
];

export function mockFlights(): Flight[] {
  seq = 0;
  const nowMs = Date.now();
  return ROWS.map((r) => {
    /*
     * Whether a movement has happened is read from the CLOCK, not written
     * into the row.
     *
     * With it hard-coded, the sample's evening bank never flew however late
     * anybody looked: at eleven at night the whole board read "please ask
     * staff", which is the display being honest about a fixture that had
     * decayed. A movement whose time has gone by has gone; the ones
     * deliberately left open — the cancellation and the stale estimate — are
     * the exceptions, and they are exceptions on purpose.
     */
    /*
     * A stale estimate is stale against the CLOCK, so it is placed against
     * the clock: scheduled three hours ago, estimated two, and never updated
     * since. Every other row keeps the schedule's own hours, which is what a
     * schedule is.
     */
    const scheduled = r.staleEstimate
      ? new Date(nowMs - 3 * 3_600_000).toISOString()
      : r.fromNow !== undefined
        ? new Date(nowMs + r.fromNow * 60_000).toISOString()
        : at(r.sched[0], r.sched[1], r.forecast ? 1 : 0);
    const shifted = r.staleEstimate
      ? new Date(nowMs - 2 * 3_600_000).toISOString()
      : r.late
        ? new Date(Date.parse(scheduled) + r.late * 60_000).toISOString()
        : scheduled;
    const flown = !r.cancelled && !r.forecast && !r.staleEstimate && !r.doorsOpen && Date.parse(shifted) <= nowMs;
    return {
      id: id(),
      direction: r.dir,
      carrier: r.carrier,
      number: r.number,
      aircraftIcao: r.icao,
      size: r.size,
      otherAirport: r.other,
      scheduledAt: scheduled,
      /* an estimate only where the movement has not happened */
      estimatedAt: flown || r.cancelled ? undefined : r.late || r.staleEstimate ? shifted : scheduled,
      actualAt: flown ? shifted : undefined,
      turnaround: r.turn,
      serviceType: r.service ?? "pax",
      source: r.forecast ? "forecast" : "schedule",
      delayCode: r.code,
      notices: r.notices?.map(([before, source, by, late, code]) => ({
        at: new Date(Date.parse(scheduled) - before * 60_000).toISOString(),
        source,
        by,
        newTime: new Date(Date.parse(scheduled) + late * 60_000).toISOString(),
        code,
      })),
      delayNote: r.note,
      seats: r.seats,
      passengers: r.pax,
      cancelled: r.cancelled
        ? { at: at(r.sched[0] - 3, 0), by: "Operations duty manager", reason: r.cancelled }
        : undefined,
    } satisfies Flight;
  });
}

/* ── the stands ───────────────────────────────────────────────────────── */

const STANDS: Stand[] = [
  /*
   * The main terminal's contact stands, 101 to 121, as Madinah numbers them
   * (the survey drawing carries the same numbers, 104L–121R). Each has a jet
   * bridge and a gate. Their sizes and gate numbers are the sample's, not the
   * airport's: only 106 and 107 take a code-E widebody here, which is what
   * makes the widebody a question.
   */
  { code: "101", name: "Stand 101", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "1" },
  { code: "102", name: "Stand 102", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "2" },
  { code: "103", name: "Stand 103", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "3" },
  { code: "104", name: "Stand 104", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "4" },
  { code: "105", name: "Stand 105", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "5" },
  { code: "106", name: "Stand 106", kind: "contact", maxSize: "E", zoneCode: "APRON-1", bridge: true, gate: "6" },
  { code: "107", name: "Stand 107", kind: "contact", maxSize: "E", zoneCode: "APRON-1", bridge: true, gate: "7" },
  { code: "108", name: "Stand 108", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "8" },
  { code: "109", name: "Stand 109", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "9" },
  { code: "110", name: "Stand 110", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "10" },
  { code: "111", name: "Stand 111", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "11" },
  { code: "112", name: "Stand 112", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "12" },
  { code: "113", name: "Stand 113", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "13" },
  { code: "114", name: "Stand 114", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "14" },
  { code: "115", name: "Stand 115", kind: "contact", maxSize: "C", zoneCode: "APRON-1", bridge: true, gate: "15" },
  { code: "116", name: "Stand 116", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "16" },
  { code: "117", name: "Stand 117", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "17" },
  { code: "118", name: "Stand 118", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "18" },
  { code: "119", name: "Stand 119", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "19" },
  { code: "120", name: "Stand 120", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "20" },
  { code: "121", name: "Stand 121", kind: "contact", maxSize: "D", zoneCode: "APRON-1", bridge: true, gate: "21" },
  /* the exclusion pair: opening one closes the other */
  {
    code: "R1",
    name: "Remote 1",
    kind: "remote",
    maxSize: "E",
    zoneCode: "APRON-R",
    excludes: ["R2"],
  },
  {
    code: "R2",
    name: "Remote 2",
    kind: "remote",
    maxSize: "C",
    zoneCode: "APRON-R",
    excludes: ["R1"],
  },
  { code: "R3", name: "Remote 3", kind: "remote", maxSize: "C", zoneCode: "APRON-R" },
  { code: "R4", name: "Remote 4", kind: "remote", maxSize: "C", zoneCode: "APRON-R" },
  /* the one the sample's pavement defect closes; see WO-000003 */
  { code: "85", name: "Stand 85", kind: "remote", maxSize: "D", zoneCode: "APRON-2" },
  { code: "F1", name: "Freight 1", kind: "cargo", maxSize: "E", zoneCode: "APRON-C" },
  { code: "G1", name: "GA 1", kind: "ga", maxSize: "B", zoneCode: "APRON-G" },
  {
    code: "M1",
    name: "Maintenance apron",
    kind: "maintenance",
    maxSize: "C",
    zoneCode: "APRON-M",
    outOfService: { why: "Line maintenance hangar works until the end of the month", by: "Engineering" },
  },
];

/** Where the sample's flights are planned to park, collisions included. */
export function mockStands(): StandRegister {
  const flights = mockFlights();
  const find = (carrier: string, number: string, dir: "arrival" | "departure") =>
    flights.find((f) => f.carrier === carrier && f.number === number && f.direction === dir);

  let n = 0;
  const plan = (
    standCode: string,
    carrier: string,
    number: string,
    dir: "arrival" | "departure",
    fromHour: number,
    fromMin: number,
    minutes: number,
    actual = false,
  ) => {
    const f = find(carrier, number, dir);
    if (!f) return null;
    const from = at(fromHour, fromMin);
    return {
      id: `sa-${String(++n).padStart(3, "0")}`,
      standCode,
      flightId: f.id,
      from,
      to: new Date(Date.parse(from) + minutes * 60_000).toISOString(),
      actual,
      by: "Stand planning",
    };
  };

  const assignments = [
    plan("101", "SV", "1401", "arrival", 5, 40, 70, true),
    plan("102", "XY", "331", "arrival", 6, 45, 75, true),
    plan("103", "MS", "663", "arrival", 6, 25, 80, true),
    plan("106", "GF", "8721", "arrival", 8, 35, 135, true),
    /*
     * 104 twice, overlapping. The turnaround before it ran long and nobody
     * moved the one behind it — which is exactly how a stand comes to be
     * double-booked in a real plan.
     */
    plan("104", "J9", "617", "arrival", 11, 20, 95),
    plan("104", "FZ", "813", "arrival", 12, 30, 75),
    plan("105", "UL", "225", "arrival", 14, 15, 110),
    plan("R3", "TK", "1907", "arrival", 14, 52, 85),
    plan("F1", "SV", "9612", "arrival", 2, 30, 105, true),
    /* R1 and R2 cannot both be used; the plan has R2 while R1 is free */
    plan("R2", "PK", "743", "arrival", 15, 45, 90),
  ].filter((a): a is NonNullable<typeof a> => a !== null);

  /*
   * The live pair gets a contact stand, so the public board has a gate to
   * show and the boarding states have somewhere to board at. Placed against
   * the clock like the movements themselves.
   */
  const anchored: [string, string, string, number, number][] = [
    /* flight, direction, stand, minutes ago it came on stand, minutes it holds */
    ["1479", "departure", "102", 45, 110],
    ["671", "arrival", "103", 50, 95],
    ["355", "arrival", "101", 95, 90],
    /* the delayed SV1483 sits on 107, and SV1478 is planned onto 107 after it */
    ["1483", "departure", "107", 60, 90],
    ["1491", "departure", "R4", 70, 70],
  ];
  for (const [number, direction, standCode, ago, holds] of anchored) {
    const f = flights.find((x) => x.number === number && x.direction === direction);
    if (!f) continue;
    const from = new Date(Date.now() - ago * 60_000).toISOString();
    assignments.push({
      id: `sa-${String(++n).padStart(3, "0")}`,
      standCode,
      flightId: f.id,
      from,
      to: new Date(Date.parse(from) + holds * 60_000).toISOString(),
      actual: true,
      by: "Stand planning",
    });
  }

  /* the arrival planned onto 107 once SV1483 was due to leave it */
  const next = flights.find((x) => x.number === "1478" && x.direction === "arrival");
  if (next) {
    const from = new Date(Date.now() + 55 * 60_000).toISOString();
    assignments.push({
      id: `sa-${String(++n).padStart(3, "0")}`,
      standCode: "107",
      flightId: next.id,
      from,
      to: new Date(Date.parse(from) + 75 * 60_000).toISOString(),
      actual: false,
      by: "Stand planning",
    });
  }

  return { stands: STANDS, assignments };
}
